import React from 'react';
import { HelpCircle, Droplet, Zap, Truck, AlertTriangle, ThermometerSun } from 'lucide-react';

/**
 * Компонент с полезными советами по бетонным работам
 */
const TipsSidebar = ({ projectType, concreteGrade }) => {
  // Общие советы для всех типов проектов
  const generalTips = [
    {
      icon: Droplet,
      title: 'Состав смеси',
      description: 'Соотношение цемента, песка и щебня влияет на прочность бетона. Стандартный состав: 1:3:5 для M200.'
    },
    {
      icon: ThermometerSun,
      title: 'Температурный режим',
      description: 'Оптимальная температура заливки от +5°C до +25°C. При низких температурах используйте противоморозные добавки.'
    },
    {
      icon: Zap,
      title: 'Правильное уплотнение',
      description: 'Используйте вибратор для ликвидации воздушных полостей. Это повышает прочность бетона на 15-20%.'
    },
    {
      icon: Truck,
      title: 'Непрерывность заливки',
      description: 'Планируйте заливку так, чтобы весь объем был залит за один раз для минимизации холодных швов.'
    }
  ];

  // Специфичные советы по типам проектов
  const projectTypeTips = {
    foundation: [
      {
        icon: AlertTriangle,
        title: 'Усиленное армирование',
        description: 'Для фундаментов используйте арматуру диаметром не менее 10-12 мм с шагом 15-20 см.'
      },
      {
        icon: Droplet,
        title: 'Гидроизоляция',
        description: 'Используйте гидроизоляционные материалы для защиты фундамента от грунтовых вод.'
      }
    ],
    floor: [
      {
        icon: Zap,
        title: 'Маяки для стяжки',
        description: 'Установите маяки для контроля уровня бетонной стяжки и используйте правило для выравнивания.'
      },
      {
        icon: ThermometerSun,
        title: 'Уход после заливки',
        description: 'В течение 7 дней поддерживайте влажность поверхности, особенно в жаркое время.'
      }
    ],
    wall: [
      {
        icon: AlertTriangle,
        title: 'Прочность опалубки',
        description: 'Опалубка должна выдерживать боковое давление бетона. Используйте стяжки и подпорки.'
      },
      {
        icon: Droplet,
        title: 'Защита от высыхания',
        description: 'При заливке стен используйте пластификаторы или укрывайте поверхность пленкой.'
      }
    ],
    column: [
      {
        icon: Zap,
        title: 'Вертикальное армирование',
        description: 'Для колонн используйте вертикальные стержни диаметром 12-14 мм с хомутами каждые 20-30 см.'
      },
      {
        icon: AlertTriangle,
        title: 'Контроль вибрации',
        description: 'При уплотнении колонн контролируйте время вибрирования, чтобы не допустить расслоения.'
      }
    ],
    pool: [
      {
        icon: Droplet,
        title: 'Водонепроницаемость',
        description: 'Для бассейнов используйте гидрофобные добавки и марку бетона не ниже М300/В25.'
      },
      {
        icon: ThermometerSun,
        title: 'Особый режим твердения',
        description: 'Бетон для бассейна должен твердеть не менее 28 дней перед нанесением гидроизоляции.'
      }
    ]
  };

  // Выбираем советы в зависимости от типа проекта
  const specificTips = projectTypeTips[projectType] || [];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <HelpCircle className="w-5 h-5 mr-2 text-cyan-500" />
        Советы по заливке бетона
      </h3>

      <div className="space-y-3">
        {/* Сначала показываем специфичные советы */}
        {specificTips.map((tip, index) => (
          <div 
            key={`specific-${index}`} 
            className="flex items-start space-x-3 p-3 bg-cyan-50 dark:bg-cyan-900/30 rounded-lg border border-cyan-100 dark:border-cyan-800 hover:bg-cyan-100 dark:hover:bg-cyan-900/40 transition-colors"
          >
            <tip.icon className="w-5 h-5 text-cyan-500 mt-1 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                {tip.title}
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {tip.description}
              </p>
            </div>
          </div>
        ))}

        {/* Затем показываем общие советы */}
        {generalTips.map((tip, index) => (
          <div 
            key={`general-${index}`} 
            className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <tip.icon className="w-5 h-5 text-gray-500 mt-1 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                {tip.title}
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {tip.description}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg">
        <div className="flex items-start space-x-3">
          <HelpCircle className="w-5 h-5 text-blue-500 mt-1 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-semibold text-blue-700 dark:text-blue-300 mb-2">
              Нужна помощь специалиста?
            </h4>
            <p className="text-xs text-blue-600 dark:text-blue-400 mb-3">
              При сложных проектах всегда лучше проконсультироваться со специалистом по бетонным работам.
            </p>
            <button className="w-full py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors">
              Найти подрядчика
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TipsSidebar;